import {
  Box,
  HStack,
  Heading,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import EditTimelineModal from "./EditTimelineModal";
import dayjs, { Dayjs } from "dayjs";
import { EventDetailTimelinesSection } from "common/interfaces/KYDEvent";
import { EditModalType, formatDate } from "../../../utils";
import { FiEdit2 } from "react-icons/fi";
import { NoVenueBanner } from "../Venue/NoVenueBanner";
import { useLoading } from "../../hooks/form";
import { SectionProps } from "../sections/sectionsComponents";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import ReschedulingCampaignModal from "../ReschedulingCampaign/ReschedulingCampaignModal";
import { useState } from "react";
import actions from "src/actions";
import { ReschedulingFormValues } from "../ReschedulingCampaign/schema";
import EventDetailTitle from "../../EventDetailTitle";

const Stat = ({ label, value, headerSize, subtext, ...boxProps }) => {
  return (
    <Box
      px={{ base: "4", md: "4" }}
      py={{ base: "5", md: "4" }}
      bg="bg-surface"
      borderRadius="lg"
      borderWidth={"1px"}
      borderColor="gray.200"
      {...boxProps}
    >
      <Stack>
        <Text fontSize="sm" color="muted">
          {label}
        </Text>
        <Heading size={headerSize}>{value}</Heading>
        {subtext ? <Text fontSize={"xs"}>{subtext}</Text> : null}
      </Stack>
    </Box>
  );
};

const translations = {
  announce_at: "📢 Announce on",
  on_sale_at: "💰 On sale",
  off_sale_at: "🛑 Off sale",
  start_at: "🎬 Event start",
  end_at: "🏁 Event end",
  doors_at: "🚪 Doors open",
};

const generateDiffString = (dateTo: Dayjs, eventStartsDate: Dayjs) => {
  const diff = eventStartsDate?.to(dateTo, true);
  const numericalDiff = eventStartsDate?.diff(dateTo, "ms");

  if (!diff) return;

  let when = numericalDiff > 0 ? "after" : "before";

  return `${diff} ${when} the event starts.`;
};

const TimelineSection = ({
  data,
  onUpdateEvent,
  permissions,
  setModalType,
  isLoading,
  modalType,
}: SectionProps<EventDetailTimelinesSection["data"]>) => {
  const [loading, updateLoading] = useLoading({
    timelines: false,
  });
  const toast = useToast();
  const [updatedTimelineData, setUpdatedTimelineData] = useState<
    EventDetailTimelinesSection["data"] | null
  >(null);
  const dates = [
    "announce_at",
    "on_sale_at",
    "start_at",
    "off_sale_at",
    "end_at",
  ];

  const hasVenue = !!data?.venue;
  const isDisabled = !hasVenue || isLoading;
  const isEventLive = data.status === "OPEN";

  if (!data) {
    return null;
  }

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  const onCancelTimelineModal = () => setModalType(EditModalType.INACTIVE);

  const onCancelReschedulingCampaignModal = () => {
    setModalType(EditModalType.TIMELINE);
  };

  const openEditModal = () => setModalType(EditModalType.TIMELINE);

  const updateTimeline = async (data, onSuccess?: () => void) => {
    updateLoading("timelines", true);
    try {
      const result = await onUpdateEvent(
        "timeline",
        JSON.stringify(data),
        onSuccess
      );
      return result;
    } finally {
      updateLoading("timelines", false);
    }
  };

  const onSaveTimelineModal = async (updatedData) => {
    // TODO: uncomment to launch the rescheduling campaign modal
    // if (
    //   isEventLive &&
    //   !dayjs(updatedData.start_at).isSame(dayjs(data.start_at), "millisecond")
    // ) {
    //   setUpdatedTimelineData(updatedData);
    //   setModalType(EditModalType.RESCHEDULING_CAMPAIGN);
    //   return;
    // }
    return updateTimeline(updatedData);
  };

  const onTimelineUpdateSuccess = async (formData: ReschedulingFormValues) => {
    const res = await sendReschedulingCampaign(data.id, formData);
    if (res) {
      toast({
        title: "Rescheduling campaign sent",
        status: "success",
      });
    }
  };
  const onSaveReschedulingCampaignModal = async (
    formData: ReschedulingFormValues
  ) => {
    // saves timeline + sends rescheduling campaign
    setModalType(EditModalType.INACTIVE);
    updateTimeline(updatedTimelineData, () =>
      onTimelineUpdateSuccess(formData)
    );
  };

  const sendReschedulingCampaign = async (
    event_id: string,
    data: ReschedulingFormValues
  ) => {
    updateLoading("reschedulingCampaign", true);
    try {
      const response = await actions.ADMIN.sendRescheduledCampaign(
        event_id,
        data
      );
      return response;
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    } finally {
      updateLoading("reschedulingCampaign", false);
    }
  };

  return (
    <Box>
      <EditTimelineModal
        isEventLive={isEventLive}
        isLoading={loading.timelines || isLoading}
        data={data}
        isOpen={modalType === EditModalType.TIMELINE}
        onCancel={onCancelTimelineModal}
        onSave={onSaveTimelineModal}
      />
      {/* {data.start_at && updatedTimelineData?.start_at && (
        <ReschedulingCampaignModal
          updatedStartAt={updatedTimelineData?.start_at}
          data={data}
          isOpen={modalType === EditModalType.RESCHEDULING_CAMPAIGN}
          onCancel={onCancelReschedulingCampaignModal}
          onSave={onSaveReschedulingCampaignModal}
          onSend={sendReschedulingCampaign} // for sending test campaign
        />
      )} */}
      <VStack alignItems={"flex-start"} w="full" spacing={1}>
        <HStack>
          <EventDetailTitle>Timeline</EventDetailTitle>
          <ShowIfHasPermission permissions={permissions} checkFor="write">
            <IconButton
              isDisabled={isLoading || loading["timelines"] || !hasVenue}
              variant={"ghost"}
              aria-label="Add affiliates"
              icon={<FiEdit2 fontSize="1.25rem" />}
              onClick={() => openEditModal()}
            />
          </ShowIfHasPermission>
        </HStack>
        <Stack
          w="100%"
          align={"flex-end"}
          sx={{
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          <SimpleGrid w="100%" columns={{ base: 1, md: 3 }} gap="6">
            {dates.map((date) => (
              <Stat
                onClick={() =>
                  isDisabled
                    ? {}
                    : hasPermission("write")
                    ? openEditModal()
                    : {}
                }
                key={date}
                isDisabled={isDisabled}
                cursor={hasPermission("write") ? "pointer" : "initial"}
                headerSize={"sm"}
                label={translations[date]}
                subtext={
                  !["start_at"].includes(date)
                    ? generateDiffString(
                        dayjs(data?.start_at),
                        data[date] ? dayjs(data[date]) : null
                      )
                    : null
                }
                value={
                  data[date]
                    ? formatDate(
                        dayjs(data[date]).tz(data?.timezone),
                        "ddd, MMM D, YYYY [at] h:mmA"
                      )
                    : "Not set"
                }
              />
            ))}
          </SimpleGrid>
        </Stack>
        <NoVenueBanner show={!hasVenue} type="timeline" />
      </VStack>
    </Box>
  );
};

export default TimelineSection;
